import { AppBar, Box, Toolbar, useMediaQuery, useTheme, IconButton, Drawer, List, ListItemButton, ListItemText, Divider, ListItemIcon, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import Logo from "./Logo";
import HeaderTopLink from "./sub-components/HeaderTopLink";
import CustomButton from "./sub-components/CustomButton";
import { useNotification } from "../context/NotificationContext";
import { useState } from "react";
import CheckoutModal from "./CheckoutModal";

export default function Header() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showNotification } = useNotification();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string>("");
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const handleReserve = (anchor: string) => {
    const waitlistSection = document.getElementById(anchor);
    if (waitlistSection) {
      waitlistSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleCheckout = () => {
    const checkoutUrl = import.meta.env.VITE_STRIPE_CHECKOUT_URL as string | undefined;
    if (!checkoutUrl) {
      showNotification("Checkout link is not configured. Please set VITE_STRIPE_CHECKOUT_URL.", "error");
      return;
    }
    // showNotification("Redirecting to secure checkout...", "info");
    // Open strictly in a new blank tab via synthetic anchor to avoid dual navigation
    const a = document.createElement("a");
    a.href = checkoutUrl;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const openMenu = () => setMenuOpen(true);
  const closeMenu = () => setMenuOpen(false);
  const navigateTo = (anchor: string) => {
    closeMenu();
    setActiveMenu(anchor);
    handleReserve(anchor);
  };

  return (
    <AppBar
      sx={{
        background: "#FFFFFF",
        boxShadow: "none",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          width: "100%",
          minHeight: { xs: 56, sm: 64, md: 64 },
          px: { xs: 2, sm: 3, md: 5 },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxSizing: "border-box",
        }}
      >
        {/* BOTEL Logo with H icon */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Logo />
        </Box>

        {/* Navigation Menu + Reserve */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {!isMobile ? (
            <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { sm: 3, md: 4 },
              }}
            >
                <HeaderTopLink name='Earning' fn={() => handleReserve("earn24-7-section")} />
                <HeaderTopLink name='Security' fn={() => handleReserve("security-section")} />
                <HeaderTopLink name='How it Works' fn={() => handleReserve("presale-works-section")} />
                <HeaderTopLink name="Founder's Club" fn={() => handleReserve("join-founders-club-section")} />
              </Box>
              <CustomButton name='Reserve Now' fn={() => { setCheckoutOpen(true); }} />
            </>
          ) : (
            <>
              <IconButton aria-label='open menu' onClick={openMenu} size='large' sx={{ backgroundColor: "#FE6F61", color: "#FFFFFF"}}>
                <MenuIcon />
              </IconButton>
              <Drawer anchor='top' open={menuOpen} onClose={closeMenu} PaperProps={{ sx: { width: '100%', height: { xs: '55vh', sm: '55vh' }, borderRadius: '0 0 16px 16px' } }}>
                <Box sx={{ p: 2, position: 'relative', height: '100%' }}>
                  <IconButton aria-label='close menu' onClick={closeMenu} sx={{ position: 'absolute', top: 8, right: 8, bgcolor: '#FE6F61', color: '#FFFFFF', '&:hover': { bgcolor: '#FE6F61' } }}>
                    <CloseIcon />
                  </IconButton>
                  <List sx={{ my: {xs: 7, sm: 9} }}>
                    <ListItemButton onClick={() => navigateTo("earn24-7-section")} sx={{ '&:hover .menuText': { color: '#FE6F61' }, '&:hover .menuDot': { opacity: 1 } }}>
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        <Box className='menuDot' sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#FE6F61', opacity: activeMenu === 'earn24-7-section' ? 1 : 0, transition: 'opacity 0.2s' }} />
                      </ListItemIcon>
                      <ListItemText className='menuText' primary='Earning' primaryTypographyProps={{ sx: { color: activeMenu === 'earn24-7-section' ? '#FE6F61' : undefined } }} />
                    </ListItemButton>
                    <ListItemButton onClick={() => navigateTo("security-section")} sx={{ '&:hover .menuText': { color: '#FE6F61' }, '&:hover .menuDot': { opacity: 1 } }}>
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        <Box className='menuDot' sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#FE6F61', opacity: activeMenu === 'security-section' ? 1 : 0, transition: 'opacity 0.2s' }} />
                      </ListItemIcon>
                      <ListItemText className='menuText' primary='Security' primaryTypographyProps={{ sx: { color: activeMenu === 'security-section' ? '#FE6F61' : undefined } }} />
                    </ListItemButton>
                    <ListItemButton onClick={() => navigateTo("presale-works-section")} sx={{ '&:hover .menuText': { color: '#FE6F61' }, '&:hover .menuDot': { opacity: 1 } }}>
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        <Box className='menuDot' sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#FE6F61', opacity: activeMenu === 'presale-works-section' ? 1 : 0, transition: 'opacity 0.2s' }} />
                      </ListItemIcon>
                      <ListItemText className='menuText' primary='How it Works' primaryTypographyProps={{ sx: { color: activeMenu === 'presale-works-section' ? '#FE6F61' : undefined } }} />
                    </ListItemButton>
                    <ListItemButton onClick={() => navigateTo("join-founders-club-section")} sx={{ '&:hover .menuText': { color: '#FE6F61' }, '&:hover .menuDot': { opacity: 1 } }}>
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        <Box className='menuDot' sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#FE6F61', opacity: activeMenu === 'join-founders-club-section' ? 1 : 0, transition: 'opacity 0.2s' }} />
                      </ListItemIcon>
                      <ListItemText className='menuText' primary="Founder's Club" primaryTypographyProps={{ sx: { color: activeMenu === 'join-founders-club-section' ? '#FE6F61' : undefined } }} />
                    </ListItemButton>
                  </List>
                  
                  <CustomButton name='Reserve Now' fn={() => { setCheckoutOpen(true); }} sx={{ width: '100%', my:2 }} />
            </Box>
              </Drawer>
            </>
          )}
        </Box>
        <CheckoutModal
          open={checkoutOpen}
          onClose={() => setCheckoutOpen(false)}
          onConfirm={handleCheckout}
        />
      </Toolbar>
    </AppBar>
  );
}
